/**
 * @module app.shared
 */

import { autoinject, bindable, computedFrom } from 'aurelia-framework';
import { State, IForm } from 'app/shared/lib/essette-site';
import { AppLogger } from 'app/shared/lib/logging/app-logger';
import { fireEvent } from 'app/shared/lib/fire-events';
import { validate } from 'app/shared/elements/ess-rule';
import { confirmation } from 'app/shared/elements/ess-confirm';


interface IFormSuccessCustomEvent { form: IForm, changes?: any }
export type FormSuccessCustomEvent = CustomEvent<IFormSuccessCustomEvent>;

interface IFormCancelCustomEvent { form: IForm }
export type FormCancelCustomEvent = CustomEvent<IFormCancelCustomEvent>;

@autoinject
export class EssForm implements IForm {

    ////****************** Fields and Properties ************************************////
    @bindable
    public target: any = null;

    @bindable
    public showRequiredFieldsMessage: boolean = true;

    @computedFrom('targetForm')
    public get state(): State {
        return this.targetForm && this.targetForm.state || this.originalState;
    }

    private targetForm: IForm | null = null;
    private readonly originalState: State = new State();

    ////****************** Element References ***************************************////
    // INFO [Matt] The actual outer form element.
    protected form!: HTMLFormElement;


    ////****************** Lifecycle events (in order of execution) *****************////
    constructor(private readonly element: Element, private readonly logger: AppLogger) {
        this.logger.source = this;
    }

    protected bind(bindingContext: any, overrideContext: any): void {
        if(!!this.target) {
            this.targetChanged();
        }
    }


    ////****************** Events ***************************************************////


    ////****************** Functions ************************************************////
    public hasChanges(): boolean {
        if(this.targetForm && !!this.targetForm.hasChanges) {
            return this.targetForm.hasChanges();
        }
        return false;
    }

    public async submit(): Promise<boolean> {
        if(!validate(this.form)) {
            return false;
        }
        if(this.targetForm && this.element) {
            const success = await this.targetForm.submit();
            if(success) {
                const detail: IFormSuccessCustomEvent = {
                    form: this.targetForm,
                    changes: this.targetForm.submittedChanges
                };
                fireEvent(this.element, 'form-success', detail);
                return true;
            }
        }
        return false;
    }

    public async cancel(): Promise<boolean> {
        if(this.hasChanges()) {
            const result = await confirmation.showCancelChanges();
            if(result.dialogResponse === confirmation.options.CANCEL) {
                return false;  //Cancelled the Cancel
            }
        }
        this.reset();
        if(this.targetForm) {
            const detail: IFormCancelCustomEvent = {
                form: this.targetForm
            };
            fireEvent(this.element, 'form-cancel', detail);
        }
        return true;
    }

    public reset(): void {
        if(this.targetForm && !!this.targetForm.reset) {
            this.targetForm.reset();
        }
    }

    public isForm(viewModel: unknown): viewModel is IForm {
        if(viewModel && typeof viewModel === 'object') {
            const model = viewModel as any;
            if(!(model.state instanceof State)) { return false; }
            if(!(model.submit instanceof Function)) { return false; }
            if(!(model.hasChanges instanceof Function)) { return false; }
            return true;
        }
        return false;
    }

    protected handleSubmit(event?: Event): void {
        if(!this.state.saving && !this.state.loading) {
            this.submit();
        }
    }

    protected handleKeyDown(e: KeyboardEvent): boolean {
        if(e.keyCode === 13 && e.ctrlKey) {
            this.handleSubmit();
            return false;
        }
        return true;
    }

    protected targetChanged(): void {
        let form: unknown = this.target;
        if(this.isForm(form)) {
            // view model seems to implement IForm
            this.targetForm = form;
        }
        else {
            if(this.target) {
                const target = this.target.constructor && this.target.constructor.name || this.target;
                this.logger.error(`<ess-form> Target Form '${target}' does not implement 'IForm' interface.`);
            }
            // view model doesn't appear to implement IForm
            this.targetForm = null;
        }
    }

    ////****************** Service Calls ********************************************////

}
